import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ChefHat, ClipboardList, FileText, AlertTriangle, Package, ArrowUpRight,
  Activity as ActivityIcon, TrendingUp, BarChart3, ShoppingBag,
} from "lucide-react";
import { StatusBadge } from "./_shared";
import { LowStockRulesCard } from "../components/LowStockRulesCard";

const TYPE_ICONS = {
  low_stock: AlertTriangle,
  daily_report: BarChart3,
  packaging_request: Package,
  packaging_approved: Package,
  packaging_rejected: Package,
  packaging_fulfilled: Package,
  order: ShoppingBag,
};

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [today, setToday] = useState(null);
  const [orders, setOrders] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats").then((r) => setStats(r.data)),
      api.get("/dashboard/today").then((r) => setToday(r.data)),
      api.get("/orders").then((r) => setOrders(r.data.slice(0, 5))),
      api.get("/inventory/low-stock").then((r) => setLowStock(r.data.slice(0, 6))),
      api.get("/notifications?limit=6&scope=all").then((r) => setActivity(r.data.notifications)),
    ]).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kpis = [
    { label: "Active Orders", value: stats?.orders_pending ?? 0, icon: ClipboardList, link: "/orders",
      sub: stats ? `${stats.orders_total} all time` : "" },
    { label: "Low Stock", value: stats?.low_stock_count ?? 0, icon: AlertTriangle,
      accent: (stats?.low_stock_count ?? 0) > 0, link: "/kitchen",
      sub: stats ? `${stats.inventory_items} catalog items` : "" },
    { label: "Revenue Issued", value: stats ? `$${stats.invoices_total.toFixed(2)}` : "—", icon: FileText, link: "/invoices",
      sub: stats ? `${stats.invoices_count} invoice${stats.invoices_count !== 1 ? "s" : ""}` : "" },
  ];

  const todayItems = [
    { label: "Orders today", value: today?.orders_today ?? 0, icon: ShoppingBag },
    { label: "Inventory adjustments", value: today?.adjustments_today ?? 0, icon: TrendingUp },
    { label: "Low-stock alerts", value: today?.low_stock_count ?? 0, icon: AlertTriangle, tone: (today?.low_stock_count ?? 0) > 0 ? "destructive" : null },
    { label: "Reports submitted", value: today?.reports_today ?? 0, icon: BarChart3, tone: (today?.reports_today ?? 0) > 0 ? "success" : null },
    { label: "Invoices issued", value: today?.invoices_today ?? 0, icon: FileText },
  ];

  return (
    <Layout>
      <PageHeader
        eyebrow={`Welcome, ${user?.name?.split(" ")[0] || ""}`}
        title="Command Center"
        description="One look at the kitchen, the queue, and the books."
        actions={
          <Link
            to="/kitchen"
            data-testid="overview-jump-kitchen"
            className="h-11 px-5 rounded-xl bg-foreground text-background font-medium text-sm inline-flex items-center gap-2 hover:bg-foreground/90 transition-all active:scale-[0.99]"
          >
            <ChefHat className="h-4 w-4" /> Open Kitchen
          </Link>
        }
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="overview-kpis">
        {kpis.map((k, idx) => (
          <Link
            key={k.label}
            to={k.link}
            className="luxury-card p-5 group fade-up"
            style={{ animationDelay: `${idx * 40}ms` }}
            data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-start justify-between">
              <div className="label-eyebrow">{k.label}</div>
              <k.icon className={`h-4 w-4 ${k.accent ? "text-destructive" : "text-muted-foreground"} group-hover:text-foreground transition-colors`} strokeWidth={1.5} />
            </div>
            <div className="font-display text-4xl font-light mt-3 tracking-tight tabular-nums">
              {loaded ? k.value : <span className="inline-block w-12 h-8 rounded shimmer" />}
            </div>
            {k.sub && <div className="text-xs text-muted-foreground mt-1">{k.sub}</div>}
          </Link>
        ))}
      </div>

      {/* Today's operations summary */}
      <section className="luxury-card surface-warm mb-8 p-6 sm:p-7" data-testid="today-summary">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[hsl(var(--accent-gold-soft))] flex items-center justify-center">
              <ActivityIcon className="h-4 w-4 text-[hsl(var(--primary))]" />
            </div>
            <div>
              <div className="label-eyebrow">Today</div>
              <h2 className="font-display text-xl font-medium">Operations Summary</h2>
            </div>
          </div>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {todayItems.map((t, idx) => (
            <div
              key={t.label}
              className="bg-card rounded-xl border border-border p-4 fade-up"
              style={{ animationDelay: `${idx * 35}ms` }}
              data-testid={`today-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <t.icon className={`h-3.5 w-3.5 ${
                  t.tone === "destructive" ? "text-destructive" :
                  t.tone === "success" ? "text-[hsl(var(--accent-sage))]" :
                  "text-muted-foreground"
                }`} strokeWidth={1.5} />
                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{t.label}</div>
              </div>
              <div className={`font-display text-2xl font-light tabular-nums ${
                t.tone === "destructive" ? "text-destructive" :
                t.tone === "success" ? "text-[hsl(var(--accent-sage))]" : ""
              }`}>
                {loaded ? t.value : <span className="inline-block w-8 h-6 rounded shimmer" />}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent orders */}
        <div className="lg:col-span-2 luxury-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-xl font-medium">Recent Orders</h2>
            <Link to="/orders" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              All orders <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {!loaded ? (
            <div className="space-y-3">{[1,2,3].map(k => <div key={k} className="h-12 rounded-lg shimmer" />)}</div>
          ) : orders.length === 0 ? (
            <EmptyState title="No orders yet" hint="Place your first order from the Orders page." />
          ) : (
            <div className="divide-y divide-border" data-testid="overview-orders">
              {orders.map((o, idx) => (
                <div key={o.id} className="py-3.5 flex items-center justify-between fade-up"
                     style={{ animationDelay: `${idx * 40}ms` }}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">
                      {o.order_no}{o.customer ? ` · ${o.customer}` : ""}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {o.items.length} items · {new Date(o.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <StatusBadge status={o.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Low stock */}
        <div className="luxury-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-xl font-medium">Low Stock</h2>
            <Link to="/kitchen" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              Kitchen <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {!loaded ? (
            <div className="space-y-2">{[1,2,3].map(k => <div key={k} className="h-6 rounded shimmer" />)}</div>
          ) : lowStock.length === 0 ? (
            <EmptyState compact title="All stock healthy" hint="Nothing below threshold." />
          ) : (
            <ul className="space-y-3" data-testid="overview-low-stock">
              {lowStock.map((i) => (
                <li key={i.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-2.5 w-2.5 rounded-full shrink-0 ring-1 ring-border" style={{ background: i.color || "#E5E5EA" }} />
                    <span className="text-sm truncate">{i.name}</span>
                  </div>
                  <span className="text-xs text-destructive font-medium tabular-nums">{i.current_stock} / {i.low_stock_threshold}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-6"><LowStockRulesCard /></div>

      {/* Recent activity */}
      <div className="luxury-card p-6 mt-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-medium">Recent Activity</h2>
          <Link to="/activity" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            Full feed <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {activity.length === 0 ? (
          <EmptyState compact title="All clear" hint="No recent operational events." />
        ) : (
          <ul className="divide-y divide-border" data-testid="overview-activity">
            {activity.map((n) => {
              const Icon = TYPE_ICONS[n.type] || ActivityIcon;
              return (
                <li key={n.id} className="py-3 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                      n.severity === "high" ? "bg-destructive/10 text-destructive" :
                      n.severity === "medium" ? "bg-[hsl(var(--accent-gold-soft))] text-[hsl(var(--primary))]" :
                      "bg-secondary text-muted-foreground"
                    }`}>
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{n.title}</div>
                      <div className="text-xs text-muted-foreground">{n.message}</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                    {new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Layout>
  );
}

function EmptyState({ title, hint, compact = false }) {
  return (
    <div className={`text-center ${compact ? "py-6" : "py-10"}`}>
      <div className="font-medium text-sm">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}
